import type {
  UpcomingArrival,
  UpcomingArrivalsData,
} from '../../utils/client-reports/types';
import {
  EmptySection,
  EtaStatus,
  Panel,
  ReportTable,
  SectionHeader,
} from './ReportBlocks';
import { IN_TRANSIT_CLASS, titleCase } from './reportLayout';
import { useReportFormat } from './useReportFormat';

const CHART_HEIGHT = 120;

type Bar = {
  key: string;
  label: string;
  detail: string;
  shipments: number;
  pending?: boolean;
};

// Embarques por día de ETA. Si hay embarques con la ETA cumplida y sin
// arribo confirmado, van en una barra aparte al comienzo.
const DaysChart = ({ data }: { data: UpcomingArrivalsData }) => {
  const { t, number, weekday } = useReportFormat();
  const bars: Bar[] = [
    ...(data.pending > 0
      ? [
          {
            key: 'pending',
            label: t('Pendientes'),
            detail: t('sin arribo confirmado'),
            shipments: data.pending,
            pending: true,
          },
        ]
      : []),
    ...data.byDay.map((day) => {
      const { weekday: name, day: date } = weekday(day.date);
      return {
        key: day.date,
        label: name,
        detail: date,
        shipments: day.shipments,
      };
    }),
  ];
  const max = Math.max(...bars.map((bar) => bar.shipments), 1);

  return (
    <div className='overflow-x-auto'>
      <div className='flex min-w-fit items-end gap-3 border-b border-default-300 px-1'>
        {bars.map((bar) => (
          <div
            key={bar.key}
            className='flex min-w-16 flex-1 flex-col items-center gap-1'
          >
            <span className='text-xs font-semibold'>
              {bar.shipments > 0 ? number(bar.shipments) : '\u00a0'}
            </span>
            <div
              className='flex w-12 items-end'
              style={{ height: CHART_HEIGHT }}
            >
              <div
                className={`w-full rounded-t-sm ${bar.pending ? 'bg-danger-500' : IN_TRANSIT_CLASS}`}
                style={{ height: `${(bar.shipments / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className='flex min-w-fit gap-3 px-1 pt-2'>
        {bars.map((bar) => (
          <div
            key={bar.key}
            className='flex min-w-16 flex-1 flex-col items-center text-center'
          >
            <span className='text-xs font-semibold'>{bar.label}</span>
            <span
              className={`text-[11px] ${bar.pending ? 'text-danger-600' : 'text-default-500'}`}
            >
              {bar.detail}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Arribos próximos 7 días: lo que llega en los próximos días, por nave y fecha de
// arribo.
const UpcomingArrivalsView = ({
  number: sectionNumber,
  data,
}: {
  number: number;
  data: UpcomingArrivalsData;
}) => {
  const { t, number, date } = useReportFormat();
  const title = t('Arribos próximos 7 días');

  if (data.rows.length === 0) {
    return (
      <section className='flex flex-col gap-6'>
        <SectionHeader number={sectionNumber} title={title} />
        <EmptySection>
          {t('No hay arribos en los próximos {{days}} días.', {
            days: data.days,
          })}
          {data.inTransit.shipments > 0
            ? ` ${t(
                '{{shipments}} embarques en tránsito llegan más adelante.',
                {
                  shipments: number(data.inTransit.shipments),
                }
              )}`
            : null}
        </EmptySection>
      </section>
    );
  }

  return (
    <section className='flex flex-col gap-8'>
      <SectionHeader
        number={sectionNumber}
        title={title}
        aside={t('{{shipments}} embarques · {{ceq}} cajas eq.', {
          shipments: number(data.window.shipments),
          ceq: number(data.window.ceq),
        })}
      />
      <Panel title={t('Embarques por día de arribo (ETA)')}>
        <DaysChart data={data} />
      </Panel>
      <Panel title={t('Detalle por nave')}>
        <ReportTable<UpcomingArrival>
          label={title}
          rows={data.rows}
          rowKey={(row) => [row.vessel, row.eta, row.destination].join('|')}
          columns={[
            {
              key: 'vessel',
              label: t('Nave / vuelo'),
              render: (row) => (
                <div>
                  <p className='font-medium'>{row.vessel || '—'}</p>
                  {row.origin ? (
                    <p className='text-xs text-default-500'>
                      {t('desde {{origin}}', { origin: row.origin })}
                    </p>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'destination',
              label: t('Puerto de destino'),
              render: (row) =>
                row.destination ? titleCase(row.destination) : '—',
            },
            {
              key: 'eta',
              label: 'ETA',
              nowrap: true,
              render: (row) => (
                <span className='font-medium'>{date(row.eta)}</span>
              ),
            },
            {
              key: 'status',
              label: t('Estado'),
              render: (row) => <EtaStatus daysToEta={row.daysToEta} />,
            },
            {
              key: 'shipments',
              label: t('Embarques'),
              align: 'right',
              render: (row) => number(row.shipments),
            },
            {
              key: 'ceq',
              label: t('Cajas eq.'),
              align: 'right',
              render: (row) => number(row.ceq),
            },
          ]}
          total={{
            vessel: t('Total'),
            shipments: number(data.window.shipments),
            ceq: number(data.window.ceq),
          }}
        />
      </Panel>
    </section>
  );
};

export default UpcomingArrivalsView;
